import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Router, { route } from 'preact-router';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { config } from '@/config';
import { telegram } from '@/data/platform/telegram';
import { authRepo } from '@/data/repositories/auth-repo';
import { homeRepo } from '@/data/repositories/home-repo';
import { useIsAuthenticated, useLogout } from '@/features/auth/useAuth';
import { AppShell } from '@/ui/shell/AppShell';
import { applyStoredTheme, T, card } from '@/ui/tokens';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';

import { LoginScreen } from '@/features/auth/LoginScreen';
import { VerifyEmailScreen } from '@/features/auth/VerifyEmailScreen';
import { ResetPasswordScreen } from '@/features/auth/ResetPasswordScreen';
import { HomeScreen } from '@/features/home/HomeScreen';
import { ServicesScreen } from '@/features/services/ServicesScreen';
import { BuyScreen } from '@/features/services/BuyScreen';
import { OrdersScreen } from '@/features/orders/OrdersScreen';
import { OrderDetailScreen } from '@/features/orders/OrderDetailScreen';
import { ReferralsScreen } from '@/features/referrals/ReferralsScreen';
import { NewsScreen } from '@/features/news/NewsScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { WalletActivityScreen } from '@/features/wallet/WalletActivityScreen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: config.query.retry, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Root/>
    </QueryClientProvider>
  );
}

type TabKey = 'home' | 'services' | 'orders' | 'referrals' | 'news';

function Tab({ children, tab }: { children: ComponentChildren; tab: TabKey; path?: string }) {
  return <AppShell tab={tab}>{children}</AppShell>;
}

function Page({ children }: { children: ComponentChildren; path?: string }) {
  return <AppShell>{children}</AppShell>;
}

function NotFound(_: { default?: boolean }) {
  return <AppShell>
    <div style={{ padding: 40, textAlign: 'center', color: '#9aa3ad' }}>Not found.</div>
  </AppShell>;
}

function BuyRoute(props: { serviceId?: string; path?: string }) {
  return <Page><BuyScreen serviceId={props.serviceId ?? ''}/></Page>;
}
function OrderDetailRoute(props: { orderId?: string; path?: string }) {
  return <Page><OrderDetailScreen orderId={props.orderId ?? ''}/></Page>;
}

function LoginRoute(_: { path?: string }) {
  return <LoginScreen/>;
}
function VerifyEmailRoute(props: { token?: string; path?: string }) {
  return <VerifyEmailScreen token={props.token}/>;
}
function ResetPasswordRoute(props: { token?: string; path?: string }) {
  return <ResetPasswordScreen token={props.token}/>;
}

function Root() {
  const authed = useIsAuthenticated();
  const logout = useLogout();
  const [path, setPath] = useState(() => window.location.pathname);
  const shouldBootstrap = authed && !isAuthUtilityPath(path);

  const authInfo = useQuery({
    queryKey: ['auth', 'info'],
    queryFn: authRepo.info,
    staleTime: 60_000,
    enabled: shouldBootstrap,
  });
  const home = useQuery({
    queryKey: ['home'],
    queryFn: homeRepo.home,
    enabled: shouldBootstrap,
  });
  const walletLinks = useQuery({
    queryKey: ['walletLinks'],
    queryFn: homeRepo.walletLinks,
    staleTime: 60_000,
    enabled: shouldBootstrap,
  });

  useEffect(() => {
    const path = window.location.pathname;

    if (path.startsWith('/verify-email') || path.startsWith('/reset-password')) return;

    if (!authed && path !== config.routes.login) route(config.routes.login, true);
    if (authed && path === config.routes.login) route(config.routes.home, true);
  }, [authed]);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;

    let removed = false;
    let handle: { remove: () => Promise<void> } | undefined;

    void CapacitorApp.addListener('backButton', () => {
      const path = window.location.pathname;
      const rootPath = authed ? config.routes.home : config.routes.login;
      const isRoot = path === rootPath;

      if (!isRoot && window.history.length > 1) {
        window.history.back();
        return;
      }

      if (!isRoot) {
        route(rootPath, true);
        return;
      }

      void CapacitorApp.exitApp();
    }).then((listener) => {
      if (removed) void listener.remove();
      else handle = listener;
    });

    return () => {
      removed = true;
      void handle?.remove();
    };
  }, [authed]);

  if (shouldBootstrap) {
    const loading = authInfo.isPending || home.isPending;
    const failed = authInfo.isError || home.isError;

    if (loading) return <StartupScreen/>;
    if (failed) {
      return (
        <StartupErrorScreen
          onRetry={() => {
            void authInfo.refetch();
            void home.refetch();
            void walletLinks.refetch();
          }}
          onLogout={() => { void logout(); }}
        />
      );
    }
  }

  return (
    <Router onChange={(e: { url: string }) => setPath(parsePath(e.url))}>
      <LoginRoute path={config.routes.login}/>
      {config.auth.emailVerify && <VerifyEmailRoute path={config.routes.verifyEmailBase}/>}
      {config.auth.emailVerify && <VerifyEmailRoute path={config.routes.verifyEmail}/>}
      <ResetPasswordRoute path={config.routes.resetPasswordBase}/>
      <ResetPasswordRoute path={config.routes.resetPassword}/>
      <Tab path={config.routes.home}      tab="home"><HomeScreen/></Tab>
      <Tab path={config.routes.services}  tab="services"><ServicesScreen/></Tab>
      <BuyRoute path={config.routes.buy}/>
      <Tab path={config.routes.orders}    tab="orders"><OrdersScreen/></Tab>
      <OrderDetailRoute path={config.routes.orderDetail}/>
      <Page path={config.routes.walletActivity}><WalletActivityScreen/></Page>
      <Tab path={config.routes.referrals} tab="referrals"><ReferralsScreen/></Tab>
      <Tab path={config.routes.news}      tab="news"><NewsScreen/></Tab>
      <Page path={config.routes.settings}><SettingsScreen/></Page>
      <NotFound default/>
    </Router>
  );
}

function isAuthUtilityPath(path: string) {
  return path.startsWith('/verify-email') || path.startsWith('/reset-password');
}

function parsePath(url: string) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0] || config.routes.home;
  }
}

function StartupScreen() {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, background: T.bg,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, color: T.text,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 26 }}>
        <img
          src="/brand-logo.png"
          alt="Boostly"
          style={{
            width: 48,
            height: 48,
            borderRadius: 16,
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>{config.brand.name}</div>
          <div style={{
            fontSize: 10, color: T.textMute, textTransform: 'uppercase',
            letterSpacing: 1.5, fontWeight: 600,
          }}>{config.brand.tagline}</div>
        </div>
      </div>

      <div style={{ ...card, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Spinner size={22}/>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Loading dashboard</div>
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Preparing your account data.</div>
        </div>
      </div>
    </div>
  );
}

function StartupErrorScreen({ onRetry, onLogout }: { onRetry: () => void; onLogout: () => void }) {
  return (
    <div style={{
      minHeight: '100%', background: T.bg, display: 'flex', flexDirection: 'column',
      justifyContent: 'center', padding: 20,
    }}>
      <ErrorBox message="Couldn't load your dashboard." onRetry={onRetry}/>
      <button onClick={onLogout} style={{
        marginTop: 12, width: '100%', padding: 13, borderRadius: 12,
        background: 'transparent', color: T.danger, border: `1px solid ${T.line2}`,
        fontFamily: T.font, fontSize: 14, fontWeight: 700, cursor: 'pointer',
      }}>
        Log out
      </button>
    </div>
  );
}

applyStoredTheme();
telegram.ready();
